import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { formatNumber } from '../../services/utils';

interface ProgressIndicatorProps {
  current: number;
  total: number;
  className?: string;
  showPercentage?: boolean;
  showCount?: boolean;
  size?: 'small' | 'medium' | 'large';
  animationDuration?: number;
  validationKey?: string;
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  current = 0,
  total = 0,
  className = '',
  showPercentage = true,
  showCount = true,
  size = 'medium',
  animationDuration = 300,
  validationKey = 'default',
}) => {
  // State to track the displayed progress (smoothly animated)
  const [displayedProgress, setDisplayedProgress] = useState(0);

  // State to track if we're currently in a reset animation
  const [isResetting, setIsResetting] = useState(false);

  // Refs to track previous values for comparison between renders
  // This helps us detect when we need to reset or update the progress
  const prevProgressRef = useRef(0);
  const prevValidationKeyRef = useRef(validationKey);

  // Refs to store timeout IDs so we can clean them up if needed
  const resetTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate the current progress percentage
  // useMemo ensures this only recalculates when current or total changes
  const progress = useMemo(() => {
    // Safety check: prevent division by zero
    if (total <= 0) return 0;

    // Safety check: prevent negative progress
    if (current < 0) return 0;

    // Calculate percentage and ensure it never exceeds 100%
    return Math.min((current / total) * 100, 100);
  }, [current, total]);

  // Debounced progress update function
  // This prevents excessive re-renders by batching rapid progress changes
  // useCallback ensures this function reference stays stable between renders
  const debouncedProgressUpdate = useCallback((newProgress: number) => {
    // Clear any pending update to replace it with this new one
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    // Calculate how much the progress is changing
    const progressDelta = Math.abs(newProgress - prevProgressRef.current);

    // Adaptive delay based on the size of the progress jump
    // Larger jumps get longer delays so users can see the change
    let updateDelay = 16; // Default: ~60fps for smooth animation

    if (progressDelta > 10) {
      // Big jumps (>10%): slower update for better visibility
      updateDelay = 50;
    } else if (progressDelta > 5) {
      // Medium jumps (5-10%): medium delay
      updateDelay = 30;
    }

    // Schedule the progress update
    updateTimeoutRef.current = setTimeout(() => {
      setDisplayedProgress(newProgress);
    }, updateDelay);
  }, []);

  // Main effect: handles progress updates and reset detection
  useEffect(() => {
    // Get the previous values from our refs
    const prevProgress = prevProgressRef.current;
    const prevValidationKey = prevValidationKeyRef.current;

    // Detect if this is a new validation session
    // When validationKey changes, we know a new process has started
    const isNewValidationSession = validationKey !== prevValidationKey;

    // Detect significant backward progress (allowing small fluctuations)
    // Sometimes progress might decrease slightly due to recounts, but a drop
    // of more than 5% indicates we should reset
    const hasSignificantBackwardProgress = progress < (prevProgress - 5);

    // Determine if we should reset the progress bar
    // Reset when: new session starts, progress goes back to 0 from significant progress,
    // or there's a significant backward jump
    const shouldReset = isNewValidationSession ||
      (current === 0 && prevProgress > 10) ||
      hasSignificantBackwardProgress;

    if (shouldReset && !isResetting) {
      // Start the reset animation
      setIsResetting(true);
      setDisplayedProgress(0);

      // Clear any pending progress updates since we're resetting
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      // After a brief reset animation, start showing the new progress
      resetTimeoutRef.current = setTimeout(() => {
        setIsResetting(false);
        debouncedProgressUpdate(progress);
      }, 150);

    } else if (!isResetting) {
      // Normal progress update (no reset needed)
      // Only update if the change is meaningful (>0.1%) to avoid micro-updates
      if (Math.abs(progress - prevProgress) > 0.1) {
        debouncedProgressUpdate(progress);
      }
    }

    // Store current values in refs for the next render comparison
    prevProgressRef.current = progress;
    prevValidationKeyRef.current = validationKey;

    // Cleanup function: runs when component unmounts or before next effect
    // This ensures we don't have dangling timeouts
    return () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [current, total, progress, validationKey, isResetting, debouncedProgressUpdate]);

  // Additional cleanup effect: ensures timeouts are cleared on unmount
  // This is a safety measure to prevent memory leaks
  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  // Calculate the progress percentage to display (rounded for UI)
  const progressPercentage = displayedProgress;

  return (
    <div className={`progress-indicator ${className} progress-indicator--${size}`}>
      {/* Progress bar container */}
      <div className="progress-indicator__bar-container">
        {/* 
          Progress bar fill element
          - Width is controlled by displayedProgress state for smooth animations
          - Transition duration adapts based on whether we're resetting or progressing
          - During reset, we use a faster transition (150ms) for quick visual feedback
          - During normal progress, we use the configured animationDuration
        */}
        <div
          className="progress-indicator__bar-fill"
          style={{
            width: `${progressPercentage}%`,
            transitionDuration: isResetting ? '150ms' : `${animationDuration}ms`,
            // Use ease-out for reset (quick start, slow end) and ease-in-out for progress
            transitionTimingFunction: isResetting ? 'ease-out' : 'ease-in-out'
          }}
        />
      </div>

      {/* Footer section with count and percentage displays */}
      <div className="progress-indicator__footer">
        {/* Item count display: shows "X af Y" format */}
        <p className="paragraph-small--bold">
          {showCount && (formatNumber(current, '-', 0) + ' af ' + formatNumber(total, '-', 0))}
        </p>

        {/* Percentage display: shows rounded percentage */}
        <p className="paragraph-small--bold">
          {showPercentage && Math.round(progressPercentage) + '%'}
        </p>
      </div>
    </div>
  );
};
export default ProgressIndicator; 